import { compareAsc } from 'date-fns/compareAsc';
import { compareDesc } from 'date-fns/compareDesc';
import { differenceInSeconds } from 'date-fns/differenceInSeconds';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { z } from 'zod';

import { isNotUndefined } from '@optical/utils';

import { isoDatetimeToDate } from '../codecs';
import { CameraSetup } from './camera-setup';
import { phaseWithTypesSchema } from './phase';
import { sessionMetadataSchema } from './session-metadata';
import {
  SessionResourceError,
  SessionResourceUtils,
  SessionResourceWarning,
  SessionVideoResource,
  sessionResourceSchema,
} from './session-resource';

export type SessionId = Session['id'];

export type SessionWithoutMetadata = z.output<typeof sessionWithoutMetadataSchema>;
export type SessionWithoutMetadataDTO = z.input<typeof sessionWithoutMetadataSchema>;

export const sessionWithoutMetadataSchema = z.object({
  cameraSetup: z.enum(CameraSetup),
  createdAt: isoDatetimeToDate,
  end: isoDatetimeToDate,
  id: z.uuid(),
  integrationId: z.string(),
  locked: z.boolean(),
  locksAt: isoDatetimeToDate.optional(),
  resources: sessionResourceSchema.array(),
  start: isoDatetimeToDate,
  teamId: z.uuid(),
  updatedAt: isoDatetimeToDate,
});

export type Session = z.output<typeof sessionSchema>;
export type SessionDTO = z.input<typeof sessionSchema>;

export const sessionSchema = sessionWithoutMetadataSchema.extend({
  title: sessionMetadataSchema.shape.title,
  description: sessionMetadataSchema.shape.description,
  type: sessionMetadataSchema.shape.type,
});

export type SessionWithPhases = z.output<typeof sessionWithPhasesSchema>;
export type SessionWithPhasesDTO = z.input<typeof sessionWithPhasesSchema>;

export const sessionWithPhasesSchema = sessionSchema.extend({
  phases: z.array(phaseWithTypesSchema),
});

export const SessionUtils = {
  getCameraIds(session: SessionWithoutMetadata | Session): string[] {
    return session.resources
      .filter(SessionResourceUtils.isVideoResource)
      .map(({ cameraId }) => cameraId);
  },
  getDuration(session: SessionWithoutMetadata | Session): number {
    return differenceInSeconds(session.end, session.start) ?? 0;
  },
  getSessionResourceErrors(session: SessionWithoutMetadata | Session): SessionResourceError[] {
    const errorSet = new Set<SessionResourceError>();

    session.resources.forEach(({ errors }) => errors.forEach((e) => errorSet.add(e)));

    return Array.from(errorSet);
  },
  getSessionResourceWarnings(session: SessionWithoutMetadata | Session): SessionResourceWarning[] {
    const warningSet = new Set<SessionResourceWarning>();

    session.resources.forEach(({ warnings }) => warnings.forEach((w) => warningSet.add(w)));

    return Array.from(warningSet);
  },
  getVideoResources(session: SessionWithoutMetadata | Session): SessionVideoResource[] {
    return session.resources.filter(SessionResourceUtils.isVideoResource);
  },
  hasAdaptedTimes(session: SessionWithoutMetadata | Session): boolean {
    const { start, end } = session;

    const videoResources = SessionUtils.getVideoResources(session);

    const earliestVideoStart = videoResources
      .map(({ start }) => start)
      .sort((a, b) => compareAsc(a, b))
      .at(0);
    const latestVideoEnd = videoResources
      .map(({ end }) => end)
      .filter(isNotUndefined)
      .sort((a, b) => compareDesc(a, b))
      .at(0);

    const startsAfterVideo =
      isNotUndefined(earliestVideoStart) && isAfter(start, earliestVideoStart);
    const endsBeforeVideo =
      isNotUndefined(latestVideoEnd) && isNotUndefined(end) && isBefore(end, latestVideoEnd);

    return startsAfterVideo || endsBeforeVideo;
  },
  hasSessionResourceErrors(session: SessionWithoutMetadata | Session): boolean {
    return SessionUtils.getSessionResourceErrors(session).length > 0;
  },
  hasSessionResourceWarnings(session: SessionWithoutMetadata | Session): boolean {
    return SessionUtils.getSessionResourceWarnings(session).length > 0;
  },
};
